import {
  prisma,
  AvailabilitiesModel,
  AvailabilitiesType,
  MeetingParticipantType,
  MeetingStatus,
} from "client";
import { getEvent, getEventId, getEventNetworkingSetting } from "../event";
import { getExhibitorByUserId } from "../exhibitor";
import moment from "moment-timezone";

/** Company id for exhibitor/sponsor — null if user is not on a company team. */
export async function resolveAvailabilityEntityId(
  userId: string,
  eventSlug: string,
  entityModel: AvailabilitiesModel
): Promise<string | null> {
  if (entityModel === AvailabilitiesModel.USER) {
    return userId;
  }
  if (entityModel === AvailabilitiesModel.EXHIBITOR) {
    const exhibitor = await prisma.exhibitor.findFirst({
      where: {
        event: { slug: eventSlug },
        team: { some: { id: userId } },
      },
      select: { id: true },
    });
    return exhibitor?.id ?? null;
  }
  if (entityModel === AvailabilitiesModel.SPONSOR) {
    const sponsor = await prisma.sponsor.findFirst({
      where: {
        event: { slug: eventSlug },
        team: { some: { id: userId } },
      },
      select: { id: true },
    });
    return sponsor?.id ?? null;
  }
  return null;
}

export const createDefaultAvailabilities = async ({
  entityId,
  entityModel,
  eventId,
}: {
  entityId: string;
  entityModel: AvailabilitiesModel;
  eventId: string;
}) => {
  const event = await getEvent(eventId);
  if (!event) {
    throw new Error(`Event with ID ${eventId} not found.`);
  }
  const { startDate, endDate } = event;

  if (!startDate) {
    throw new Error("Event must have a valid start date.");
  }
  await deleteUserAvailabilities(entityId, eventId);
  const eventNetworkingSettings = await getEventNetworkingSetting(eventId);
  const availabilities = {
    startPeriod: startDate,
    endPeriod: endDate ?? new Date(startDate),
    entityId,
    entityModel,
    type: AvailabilitiesType.EVENT_DAYS,
    eventId: eventId,
    slotDurationInMinutes: eventNetworkingSettings?.meetingSlotDuration ?? 30,
  };

  await prisma.availability.create({
    data: availabilities,
  });
};
export const createAvailabilities = async ({
  entityId,
  entityModel,
  eventId,
  data,
}: {
  entityId: string;
  entityModel: AvailabilitiesModel;
  eventId: string;
  data: {
    startPeriod: string;
    endPeriod: string;
    slot?: number;
  }[];
}) => {
  try {
    return await Promise.all(
      data.map(async (row) => {
        const { startPeriod, endPeriod } = row;

        if (startPeriod >= endPeriod) {
          throw new Error("Start time must be before end time.");
        }

        return prisma.availability.create({
          data: {
            startPeriod,
            endPeriod,
            entityId,
            entityModel,
            eventId,
            slotDurationInMinutes: row.slot ? row.slot : 30,
            type: AvailabilitiesType.CUSTOM_DAYS,
          },
        });
      })
    );
  } catch (error) {
    console.error("Failed to create availability:", error);
    throw error;
  }
};

export const updateSlotDuration = async ({
  entityId,
  entityModel,
  newSlotDuration,
}: {
  entityId: string;
  entityModel: AvailabilitiesModel;
  newSlotDuration: number;
}) => {
  if (!newSlotDuration || newSlotDuration <= 0) {
    throw new Error("Invalid slot duration. It must be greater than 0.");
  }

  const existingEntity = await (prisma as any)[
    entityModel.toLowerCase()
  ].findUnique({
    where: { id: entityId },
  });

  if (!existingEntity) {
    throw new Error(`${entityModel} with ID ${entityId} does not exist.`);
  }

  // Fetch all existing availabilities for the entity
  const existingAvailabilities = await prisma.availability.findMany({
    where: { entityId },
  });

  if (existingAvailabilities.length === 0) {
    throw new Error(
      `No availabilities found for ${entityModel} with ID ${entityId}.`
    );
  }

  // Insert updated availabilities
  const updatedAvailabilities = await prisma.availability.updateMany({
    where: { entityId },
    data: {
      slotDurationInMinutes: newSlotDuration,
    },
  });

  return {
    message: `Slot duration updated to ${newSlotDuration} minutes for ${entityModel} with ID ${entityId}.`,
    updatedCount: updatedAvailabilities.count,
  };
};

export const getUserAvailabilities = async (
  receiverId: string,
  eventSlug: string,
  entityModel: AvailabilitiesModel
) => {
  let entityId = receiverId;
  const event = await getEventId(eventSlug);
  if (!event) {
    throw new Error(`Event with slug ${eventSlug} not found.`);
  }
  if (entityModel === AvailabilitiesModel.EXHIBITOR) {
    const exhibitor = await getExhibitorByUserId(entityId, eventSlug);

    if (exhibitor) {
      entityId = exhibitor.id;
    }
  }
  const availabilities = await prisma.availability.findMany({
    where: {
      entityId,
      eventId: event.id,
    },
  });

  return availabilities;
};

const getReceiverAvailableDays = async (
  receiverId: string,
  eventSlug: string
) => {
  const event = await getEventId(eventSlug, { timezone: true });
  if (!event) throw new Error(`Event with slug ${eventSlug} not found.`);

  const receiverAvailabilities = await prisma.availability.findMany({
    where: {
      entityId: receiverId,
      eventId: event.id,
    },
    select: {
      startPeriod: true,
      endPeriod: true,
      slotDurationInMinutes: true,
    },
    orderBy: {
      startPeriod: "asc",
    },
  });

  // Process to get unique days
  const uniqueDays = receiverAvailabilities.map((availability) => {
    // Ensure dates are properly parsed and stored in UTC
    const startDate = moment(availability.startPeriod).utc().toISOString();
    const endDate = moment(availability.endPeriod).utc().toISOString();

    return {
      startDate,
      endDate,
      slotDurationInMinutes: availability.slotDurationInMinutes,
      timeZone: event.timezone || "UTC", // Ensure we have a default timezone
    };
  });

  // Remove duplicate days
  const days = uniqueDays.filter(
    (value, index, self) =>
      index === self.findIndex((t) => t.startDate === value.startDate)
  );

  return days;
};

export const splitDayIntoSlots = (
  selectedDay: string,
  slotDuration: number,
  dayStartTime?: string,
  dayEndTime?: string
) => {
  let dayStart = moment(selectedDay).startOf("day");
  let dayEnd = moment(selectedDay).endOf("day");

  if (dayStartTime) {
    const [hours, minutes] = dayStartTime.split(":").map(Number);
    dayStart = moment(selectedDay).set({ hours, minutes, seconds: 0 });
  }

  if (dayEndTime) {
    const [hours, minutes] = dayEndTime.split(":").map(Number);
    dayEnd = moment(selectedDay).set({ hours, minutes, seconds: 59 });

    // Adjust to next day if end time is before start time
    if (dayEnd.isBefore(dayStart)) {
      dayEnd.add(1, "day");
    }
  }

  const slots = [];
  let slotStart = dayStart.clone();

  while (
    slotStart.clone().add(slotDuration, "minutes").isSameOrBefore(dayEnd)
  ) {
    const slotEnd = slotStart.clone().add(slotDuration, "minutes");
    slots.push({
      start: slotStart.toDate(),
      end: slotEnd.toDate(),
    });
    slotStart = slotEnd.clone();
  }

  return slots;
};

const splitDays = (
  startPeriod: Date,
  endPeriod: Date,
  slotDuration: number,
  timeZone: string
) => {
  const days = [];
  const startDateTime = moment(startPeriod).tz(timeZone);
  const endDateTime = moment(endPeriod).tz(timeZone);

  let currentDay = startDateTime.clone().startOf("day");

  while (currentDay.isSameOrBefore(endDateTime, "day")) {
    // Figure out today's slot window
    const isFirstDay = currentDay.isSame(startDateTime, "day");
    const isLastDay = currentDay.isSame(endDateTime, "day");

    const start = isFirstDay
      ? startDateTime.clone()
      : currentDay.clone().startOf("day");

    const end = isLastDay
      ? endDateTime.clone()
      : currentDay.clone().endOf("day");

    if (start.isBefore(end)) {
      days.push({
        // Always Latin YYYY-MM-DD — never inherit process moment.locale("ar").
        date: currentDay.clone().locale("en").format("YYYY-MM-DD"),
        start: start.toDate(),
        end: end.toDate(),
        slotDurationInMinutes: slotDuration,
        timeZone,
      });
    }

    currentDay.add(1, "day");
  }

  return days;
};

export const getReceiverAvailableSlots = async (
  receiverId: string,
  senderId: string,
  eventSlug: string,
  receiverModel: AvailabilitiesModel,
  senderModel: AvailabilitiesModel,
  selectedDay?: string
) => {
  let receiverEntityId = receiverId;

  // Fetch available days for the receiver
  const availableDays = await getReceiverAvailableDays(
    receiverEntityId,
    eventSlug
  );

  // Step 1: Get meeting IDs where sender is a participant
  const senderParticipantRecords = await prisma.meetingParticipant.findMany({
    where: {
      OR: [
        {
          entityId: senderId,
          type: MeetingParticipantType.RECEIVER,
          meeting: {
            status: MeetingStatus.ACCEPTED,
          },
        },
        {
          entityId: senderId,
          type: MeetingParticipantType.SENDER,
          meeting: {
            status: { not: MeetingStatus.REJECTED },
          },
        },
      ],
    },
    select: {
      meetingId: true,
    },
  });
  // return senderParticipantRecords;
  // Step 2: Get sender's meetings
  const senderMeetingIds = senderParticipantRecords.map((p) => p.meetingId);
  const senderMeetings = await prisma.meeting.findMany({
    where: {
      id: { in: senderMeetingIds },
    },
    select: {
      startDate: true,
      endDate: true,
    },
  });

  // Step 3: Repeat for receiver
  const receiverParticipantRecords = await prisma.meetingParticipant.findMany({
    where: {
      OR: [
        {
          entityId: receiverId,
          type: MeetingParticipantType.RECEIVER,
          meeting: {
            status: MeetingStatus.ACCEPTED,
          },
        },
        {
          entityId: receiverId,
          type: MeetingParticipantType.SENDER,
          meeting: {
            status: { not: MeetingStatus.REJECTED },
          },
        },
      ],
    },
    select: {
      meetingId: true,
    },
  });

  const receiverMeetingIds = receiverParticipantRecords.map((p) => p.meetingId);
  const receiverMeetings = await prisma.meeting.findMany({
    where: {
      id: { in: receiverMeetingIds },
    },
    select: {
      startDate: true,
      endDate: true,
    },
  });

  // Get all unique timezones from available days
  let targetTimeZone = availableDays[0]?.timeZone;

  if (!targetTimeZone) {
    targetTimeZone = "UTC";
  }

  const splitAvailabilityDays = availableDays.flatMap((availability) => {
    // Convert dates from UTC to the target timezone for display
    const startPeriod = moment
      .utc(availability.startDate)
      .tz(availability.timeZone)
      .toDate();
    const endPeriod = moment
      .utc(availability.endDate)
      .tz(availability.timeZone)
      .toDate();

    return splitDays(
      startPeriod,
      endPeriod,
      availability.slotDurationInMinutes || 30,
      availability.timeZone
    );
  });

  // Resolve day: use requested date if it exists and is not past (event TZ),
  // else first upcoming day. Never fall back to a past day for auto-pick.
  const todayInEventTz = moment().tz(targetTimeZone).startOf("day");
  const upcomingDays = splitAvailabilityDays
    .filter(
      (day) =>
        !moment
          .tz(day.date, "YYYY-MM-DD", true, targetTimeZone)
          .isBefore(todayInEventTz)
    )
    .sort((a, b) => a.date.localeCompare(b.date));

  const requestedDay =
    selectedDay && moment(selectedDay, "YYYY-MM-DD", true).isValid()
      ? selectedDay
      : undefined;

  const requestedIsBookable =
    !!requestedDay &&
    splitAvailabilityDays.some((day) => day.date === requestedDay) &&
    !moment
      .tz(requestedDay, "YYYY-MM-DD", true, targetTimeZone)
      .isBefore(todayInEventTz);

  const effectiveDay = (requestedIsBookable && requestedDay) || upcomingDays[0]?.date;

  if (!effectiveDay) {
    return {
      availableDays: splitAvailabilityDays,
      slotsForDay: [],
      selectedDay: null,
      timeZone: targetTimeZone,
    };
  }

  const availabilityWithSplitDays = availableDays.map((availability) => {
    const startPeriod = moment
      .utc(availability.startDate)
      .tz(availability.timeZone)
      .toDate();
    const endPeriod = moment
      .utc(availability.endDate)
      .tz(availability.timeZone)
      .toDate();

    return {
      ...availability,
      days: splitDays(
        startPeriod,
        endPeriod,
        availability.slotDurationInMinutes,
        availability.timeZone
      ),
    };
  });

  const dayAvailability = availabilityWithSplitDays
    .flatMap((avail) => avail.days)
    .filter((day) => effectiveDay === day.date);

  if (!dayAvailability.length) {
    return {
      availableDays: splitAvailabilityDays,
      slotsForDay: [],
      selectedDay: effectiveDay,
      timeZone: availabilityWithSplitDays[0]?.timeZone ?? targetTimeZone,
    };
  }

  const slotsForDay = dayAvailability
    .map((day) => {
      const slotDuration = day.slotDurationInMinutes || 30;
      const dayStartTime = moment(day.start).locale("en").format("HH:mm");
      const dayEndTime = moment(day.end).locale("en").format("HH:mm");
      return splitDayIntoSlots(
        effectiveDay,
        slotDuration,
        dayStartTime,
        dayEndTime
      );
    })
    .flat();

  const senderBusySlots = senderMeetings.map((m) => ({
    start: moment.utc(m.startDate).tz(targetTimeZone),
    end: moment.utc(m.endDate).tz(targetTimeZone),
  }));

  const receiverBusySlots = receiverMeetings.map((m) => ({
    start: moment.utc(m.startDate).tz(targetTimeZone),
    end: moment.utc(m.endDate).tz(targetTimeZone),
  }));

  const availableSlotsForSelectedDay = slotsForDay.filter((slot) => {
    const startTime = moment
      .utc(slot.start)
      .tz(targetTimeZone)
      .locale("en")
      .format("HH:mm");
    const endTime = moment
      .utc(slot.end)
      .tz(targetTimeZone)
      .locale("en")
      .format("HH:mm");

    const slotStart = moment.tz(
      `${effectiveDay} ${startTime}`,
      targetTimeZone
    );
    const slotEnd = moment.tz(`${effectiveDay} ${endTime}`, targetTimeZone);

    const isSlotAvailableForReceiver = !receiverBusySlots.some(
      (busySlot) =>
        slotStart.isBefore(busySlot.end) && slotEnd.isAfter(busySlot.start)
    );

    const isSlotAvailableForSender = !senderBusySlots.some(
      (busySlot) =>
        slotStart.isBefore(busySlot.end) && slotEnd.isAfter(busySlot.start)
    );

    return isSlotAvailableForReceiver && isSlotAvailableForSender;
  });

  const formattedAvailableSlots = availableSlotsForSelectedDay.map((slot) => ({
    start: moment(slot.start).tz(targetTimeZone).locale("en").format("HH:mm"),
    end: moment(slot.end).tz(targetTimeZone).locale("en").format("HH:mm"),
  }));

  return {
    availableDays: splitAvailabilityDays,
    slotsForDay: formattedAvailableSlots,
    selectedDay: effectiveDay,
    timeZone: dayAvailability[0]?.timeZone ?? targetTimeZone,
  };
};

export const deleteUserAvailabilities = async (
  entityId: string,
  eventId: string
) => {
  //TODO: check if there is no accepted meeting request before deleting

  await prisma.availability.deleteMany({
    where: {
      entityId,
      eventId,
    },
  });
};
export const deleteEventDaysUserAvailabilities = async (
  entityId: string,
  eventId: string
) => {
  //TODO: check if there is no accepted meeting request before deleting

  await prisma.availability.deleteMany({
    where: {
      entityId,
      eventId,
      type: AvailabilitiesType.EVENT_DAYS,
    },
  });
};

export const updateAvailabilitiesSlot = async (
  entityId: string,
  slot: number,
  eventId: string
) => {
  await prisma.availability.updateMany({
    where: {
      entityId,
      eventId,
    },
    data: {
      slotDurationInMinutes: slot,
    },
  });
};
